import { useReducer } from 'react'
import type { OrderVisualStatus } from '../ordersListShared'
import type { OrdersListFlowAction, OrdersListFlowState } from './OrdersListFlow.types'

type UseOrdersListFlowReducerInput = {
  initialStatusFilters: OrderVisualStatus[]
}

const createInitialState = ({ initialStatusFilters }: UseOrdersListFlowReducerInput): OrdersListFlowState => ({
  searchValue: '',
  filtersOpen: false,
  sortBy: 'recent-desc',
  dateFilter: 'all',
  customDateFrom: '',
  customDateTo: '',
  statusFilters: initialStatusFilters,
  priceMin: '',
  priceMax: '',
  pageSize: 20,
  currentPage: 1,
})

const ordersListFlowReducer = (
  state: OrdersListFlowState,
  action: OrdersListFlowAction,
): OrdersListFlowState => {
  switch (action.type) {
    case 'SET_SEARCH_VALUE': {
      return {
        ...state,
        searchValue: action.value,
      }
    }
    case 'SET_FILTERS_OPEN': {
      return {
        ...state,
        filtersOpen: action.value,
      }
    }
    case 'SET_SORT_BY': {
      return {
        ...state,
        sortBy: action.value,
      }
    }
    case 'SET_DATE_FILTER': {
      return {
        ...state,
        dateFilter: action.value,
      }
    }
    case 'SET_CUSTOM_DATE_FROM': {
      return {
        ...state,
        customDateFrom: action.value,
      }
    }
    case 'SET_CUSTOM_DATE_TO': {
      return {
        ...state,
        customDateTo: action.value,
      }
    }
    case 'SET_STATUS_FILTERS': {
      return {
        ...state,
        statusFilters: action.value,
      }
    }
    case 'TOGGLE_STATUS_FILTER': {
      if (action.checked) {
        if (state.statusFilters.includes(action.status)) return state
        return {
          ...state,
          statusFilters: [...state.statusFilters, action.status],
        }
      }

      return {
        ...state,
        statusFilters: state.statusFilters.filter((candidate) => candidate !== action.status),
      }
    }
    case 'SET_PRICE_MIN': {
      return {
        ...state,
        priceMin: action.value,
      }
    }
    case 'SET_PRICE_MAX': {
      return {
        ...state,
        priceMax: action.value,
      }
    }
    case 'SET_PAGE_SIZE': {
      return {
        ...state,
        pageSize: action.value,
      }
    }
    case 'SET_CURRENT_PAGE': {
      if (state.currentPage === action.value) return state
      return {
        ...state,
        currentPage: action.value,
      }
    }
    case 'CLEAR_ALL_FILTERS': {
      return {
        ...state,
        searchValue: '',
        dateFilter: 'all',
        customDateFrom: '',
        customDateTo: '',
        statusFilters: [],
        priceMin: '',
        priceMax: '',
        sortBy: 'recent-desc',
      }
    }
    case 'RESET_DATE_FILTER': {
      return {
        ...state,
        dateFilter: 'all',
        customDateFrom: '',
        customDateTo: '',
      }
    }
    case 'RESET_PRICE_RANGE': {
      return {
        ...state,
        priceMin: '',
        priceMax: '',
      }
    }
    default:
      return state
  }
}

export const useOrdersListFlowReducer = (input: UseOrdersListFlowReducerInput) =>
  useReducer(ordersListFlowReducer, input, createInitialState)
